import {
  type ActiveFiltersState,
  type AdvancedFilterState,
  type TabFilterState,
  type ViolationsActionManagerState,
} from '../../../shared/types';
import { type EntitlementDetailsRowType } from '../../ViolationsEntitlement/data-layer';
import {
  type EntitlementColumnState,
  type EntitlementSortState,
  type FilterCacheState,
} from '../../ViolationsEntitlement/data-layer/entitlementTypes';
import { type EntitlementConflictViolatingEntitlement } from '../components/EntitlementDetails/InherentRiskDetailsTab/RiskDetailsConflictsTab/data-layer';
import { type RiskItem } from '../components/EntitlementDetails/InherentRisksTab';
import { type UserItem } from '../components/EntitlementDetails/UsersTab';
import { type UserViolationChartDetails } from '../components/EntitlementDetails/UsersTab/data-layer';

export enum DetailsType {
  SLICE_NAME = 'aag/details',
  API_SLICE_NAME = 'aag/detailsApi',
}

export interface ViolationConflictTrackers {
  id: number;
  level: number;
  childrenCount: number;
  type: string;
}

export interface ViolationFunctions {
  id: number;
  name: string;
  description: string | null;
  accountName: string;
  endpointName: string;
  assignedEntitlementsCount: number | null;
  endpointLogo: string | null;
  displayName?: string | null;
}

export interface TrackedViolationFunctions extends ViolationFunctions {
  tracker: ViolationConflictTrackers;
}
export interface ViolationAssignedEntitlements {
  violationKey: number;
  functionKey: number;
  entitlementKey: number;
  entitlementValue: string | null;
  entitlementTypeName: string | null;
  entitlementDisplayName: string;
  entitlementTrustScore: number;
  occurrencesInFunctions: number;
  soxSeverity: string;
  sysSeverity: string;
  privilegedSeverity: string;
  startDate: string | null;
  endDate: string | null;
  totalViolatingEntitlements: number | null;
  description: string | null;
  glossary: string | null;
  customProperty1: string | null;
}
export interface TrackedViolationAssignedEntitlements
  extends ViolationAssignedEntitlements {
  tracker: ViolationConflictTrackers;
}

export interface ViolatingEntitlementObjects {
  fieldName: string;
  userValue: string;
  ruleValue: string;
  showObjectFieldLabel: boolean;
  objectValue: string;
}

export interface ViolatingEntitlements {
  violationKey: number;
  functionKey: number;
  entitlementKey: number;
  entitlementValue: string | null;
  entitlementTypeName: string | null;
  entitlementDisplayName: string;
  objectValue: string;
  entitlementTrustScore: number;
  fieldName: string | null;
  fieldValue: string;
  userValue: string | null;
  ruleValue: string | null;
  firstUsed: string;
  lastUsed: string;
  totalUsed: number | null;
  object?: ViolatingEntitlementObjects[] | null;
  showObjectFieldLabel: boolean;
  soxSeverity: string;
  sysSeverity: string;
  privilegedSeverity: string;
  startDate: string | null;
  endDate: string | null;
  description: string | null;
  glossary: string | null;
  customProperty1: string | null;
}

export interface TrackedViolatingEntitlements extends ViolatingEntitlements {
  violationId: string;
  assignedEntitlementId: string;
  tracker: ViolationConflictTrackers;
}

export interface ViolationMitigatingControls {
  id: number;
  name: string;
  approvedBy: string;
  approverName: string;
  preMitigated: boolean;
  description: string;
  controlType: string;
  comments: string;
  startDate: string;
  endDate: string;
  expiryDate: string;
  expiryStatus: 0 | 1 | 2; // 0 (Active), 1 (Expires soon), 2 (Expired)
  priority: string | number;
}
interface ViolationsTabDetailsConflictControls {
  filters: AdvancedFilterState[];
  activeFilters: ActiveFiltersState[];
  searchKey: string | null;
  expandKey: string | null;
}

export interface EntitlementRisksListState {
  isError: string | null;
  isLoading: boolean;
  data: RiskItem[];
  totalRecords: number | null;
  rowIdToTransition: number | string | null;
}

export interface EntitlementRisksControlsState {
  filters: AdvancedFilterState[];
  activeFilters: ActiveFiltersState[];
  sortInAction: EntitlementSortState;
  activeColumns: EntitlementColumnState[];
  filterCache: FilterCacheState;
  searchKey: string | null;
  activeRowContext: EntitlementRisksSidePanelRowContextState | null;
}

export interface EntitlementRisksSidePanelRowContextState {
  riskId: number;
  riskScore?: number;
  endpointName?: string;
  riskOwner?: string | null;
  riskName?: string;
  riskViolationSummaryObjects?: {
    riskScore?: number;
    violationEntitlementCount?: number;
    violationCount?: number;
  } | null;
  description?: string | null;
  priority?: string;
  riskType?: string;
  ruleset?: string;
  function1?: string | null;
  function2?: string | null;
  function3?: string | null;
  function4?: string | null;
  function5?: string | null;
  businessProcess?: string | null;
  customProperty1?: string | null;
  customProperty2?: string | null;
}

export interface EntitlementRisksSidePanelGroupRowContextState {
  current: EntitlementRisksSidePanelRowContextState;
  prev?: EntitlementRisksSidePanelRowContextState;
  next?: EntitlementRisksSidePanelRowContextState;
}

export interface EntitlementRisksSidePanelState {
  isSidePanelVisible: boolean;
  rowContext: EntitlementRisksSidePanelGroupRowContextState;
}

export interface EntitlementUsersListState {
  isError: string | null;
  isLoading: boolean;
  data: UserItem[];
  totalRecords: number | null;
  rowIdToTransition: number | string | null;
}

export interface EntitlementUsersControlsState {
  filters: AdvancedFilterState[];
  activeFilters: ActiveFiltersState[];
  sortInAction: EntitlementSortState;
  activeColumns: EntitlementColumnState[];
  filterCache: FilterCacheState;
  searchKey: string | null;
  activeRowContext: EntitlementUsersSidePanelRowContextState;
}

export interface EntitlementUsersSidePanelRowContextState {
  userKey: number | null;
  userName?: string | null;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  departmentName?: string | null;
  status?: string | null;
  systemUserName?: string | null;
  employeeId?: string | null;
  title?: string | null;
  employeeType?: string | null;
  employeeClass?: string | null;
  identityType?: string | null;
  riskScore?: number | null;
  accountsCount?: number | null;
}

export interface EntitlementUsersSidePanelGroupRowContextState {
  current: EntitlementUsersSidePanelRowContextState;
  prev?: EntitlementUsersSidePanelRowContextState;
  next?: EntitlementUsersSidePanelRowContextState;
}

export interface EntitlementUsersSidePanelState {
  isSidePanelVisible: boolean;
  rowContext: EntitlementUsersSidePanelGroupRowContextState;
}

export interface DetailsNavigationContext {
  title: string | null;
  userName: string | null;
  entName: string | null;
  riskName: string | null;
}

export type { DetailsNavigationPayload } from '../../../shared/types/navigationTypes';

export interface DetailsState {
  source: string | null;
  navigationContext: DetailsNavigationContext;
  queryParam: string | undefined;
  activeTab: string | null;
  preferredReportTab: string | null;
  sidepanel: { isSidePanelVisible: boolean };
  violationsTab: {
    isFooterVisible: boolean;
    conflicts: {
      selectedRowData: TrackedViolationFunctions | null;
      controls: ViolationsTabDetailsConflictControls;
    };
    mitigate: {
      selectedRowData: ViolationMitigatingControls | null;
      isListRefresh: boolean;
    };
    actionManager: ViolationsActionManagerState | null;
  };
  entitlementSummary: {
    selectedEntitlement: EntitlementDetailsRowType | null;
    data: EntitlementDetailsRowType | null;
    loading: boolean;
    error: string | null;
  };
  entitlementDetails: {
    data: EntitlementDetailsRowType | null;
    loading: boolean;
    error: string | null;
  };
  entitlementRisks: {
    list: EntitlementRisksListState;
    controls: EntitlementRisksControlsState;
    sidepanel: EntitlementRisksSidePanelState;
    selectedEntitlementRisk: RiskItem | null;
  };
  entitlementUsers: {
    list: EntitlementUsersListState;
    controls: EntitlementUsersControlsState;
    sidepanel: EntitlementUsersSidePanelState;
    selectedEntitlementUser: UserItem | null;
  };
  entitlementViolationsTab: {
    activeFilters: ActiveFiltersState[];
    tabFilter: TabFilterState[];
    filters: AdvancedFilterState[];
    violationsCount: number;
  };
  entitlementConflicts: {
    searchResults: {
      data: unknown[];
      isLoading: boolean;
      error: string | null;
      searchAfter: string[] | null;
      afterKey: string | null;
    };
    selectedRowData: TrackedViolationFunctions | null;
    selectedRisk: RiskItem | null;
    sidepanel: { isSidePanelVisible: boolean };
    controls: {
      filters: AdvancedFilterState[];
      activeFilters: ActiveFiltersState[];
      searchKey: string | null;
      expandKey: string | null;
    };
  };
}

// API queries
export interface ViolationConflictApisProp {
  violationId: string;
  functionId?: string;
  assignedEntitlementId?: string;
  payload: {
    violatingSoxCriticality: string[];
    violatingSysCriticality: string[];
    violatingPrivileged: string[];
    violatingEntitlements: string[];
    assignedEntitlements: string[];
    globalSearch: string;
    afterKey?: string | null;
    searchAfter?: string[] | null;
    max?: number;
  };
}

// API responses
interface GetApiResponse {
  status?: number;
  errorCode?: string;
  displayRecords?: number;
  displayCount?: number;
  displaycount?: number;
}

export interface GetViolationFunctionApiResponse extends GetApiResponse {
  data: ViolationFunctions[];
}

export interface GetAssignedEntitlementApiResponse extends GetApiResponse {
  data: ViolationAssignedEntitlements[];
  afterKey: string | null;
}

export interface GetViolationMitigatingControlApiResponse
  extends GetApiResponse {
  data: ViolationMitigatingControls[];
}

export interface GetViolatingEntitlementApiResponse extends GetApiResponse {
  data: ViolatingEntitlements;
  searchAfter: string[] | null;
}

export interface ViolatingEntitlement {
  violationKey: number;
  entitlementValue: string;
  entitlementDisplayName: string;
  entitlementTypeName: string;
  functionKey: number;
  entitlementKey: number;
}

export interface ConflictsFunctionCountData {
  data: EntitlementConflictViolatingEntitlement[];
  count: number | null;
  searchAfter: string[] | null;
  afterKey: string | null;
}

export interface RiskConflictsDisplayProps {
  activeTab: string;
  data?: EntitlementConflictViolatingEntitlement[];
  isLoading?: boolean;
  className?: string;
  triggerFetchAssignedEntitlements?: (
    violationId: string,
    functionId: string,
    afterKey: string | null,
    isOnExpand?: boolean
  ) => void;
  functionGroupedData?: Record<
    string,
    { data: EntitlementConflictViolatingEntitlement[] }
  >;
  hasMore?: boolean;
  onLoadMore?: () => Promise<void>;
  functionCount?: Record<string, ConflictsFunctionCountData> | null;
}

// SOD Violations Count Types
export interface SodViolationsCountFilters {
  sodStatuses?: string[];
  risk?: {
    riskIds?: number[];
    riskName?: string[];
    actualRisk?: string[];
    riskPriority?: string[];
    riskType?: string[];
    businessProcess?: string[];
    violationSource?: string[];
    violationAge?: {
      lessThanDays?: number;
      moreThanDays?: number;
    };
  };
  function?: {
    functionKeys?: number[];
    functionName?: string[];
    status?: string[];
    description?: string;
  };
  entitlement?: {
    entitlementValueKeys?: number[];
    value?: string[];
    entitlementType?: string[];
  };
  account?: {
    accountKeys?: number[];
    accountName?: string[];
    accountType?: string[];
    userLock?: string[];
  };
  user?: {
    userKeys?: number[];
    userName?: string[];
    firstName?: string[];
    lastName?: string[];
    costCenter?: string[];
    employeeId?: string[];
    jobCode?: string;
    departmentName?: string[];
    countryName?: string[];
  };
  ruleset?: {
    rulesetKeys?: number[];
    rulesetName?: string[];
  };
  application?: {
    endpointKeys?: number[];
    applicationName?: string[];
  };
  mitigatingControl?: {
    mitigatingControlKeys?: number[];
    mitigatingControlName?: string[];
    mcExpiryDate?: {
      startDate?: string;
      endDate?: string;
    };
    mcAssignedStartDate?: {
      startDate?: string;
      endDate?: string;
    };
    mcAssignedEndDate?: {
      startDate?: string;
      endDate?: string;
    };
  };
  globalSearch?: string;
  insightRequests?: {
    id: number;
    filters: Record<string, unknown>;
  }[];
}

export interface GetSodViolationsCountApiResponse extends GetApiResponse {
  data: UserViolationChartDetails;
  message: string;
}
